import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useExerciseFilters from "./useExerciseFilters";
import { ExerciseFiltersObject } from "../../hooks/useAdditionalHooks";
import EAModal from "../../../shared/Modal/EAModal";
import EACheckbox from "../../../shared/Checkbox/EACheckbox";
import EASelect from "../../../shared/EASelect/EASelect";
import { Assort } from "../../../../models/Assort";
import { Instructor } from "../../../../models/Instructor";
import getId from "../../../../../utils/getId";
import "./style.scss";

interface IProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  filters: ExerciseFiltersObject;
  setFilters: (filters: ExerciseFiltersObject) => void;
  dateFromState: Date;
  dateToState: Date;
}

const emptyFilters = {} as ExerciseFiltersObject;
const ALL_OPTION = "all";

const ExerciseFltersModal: React.FC<IProps> = ({ open, setOpen, filters, setFilters, dateFromState, dateToState }) => {
  const { t } = useTranslation();
  const { exerciseGroups, zones, humans } = useExerciseFilters(dateFromState, dateToState);
  const [draft, setDraft] = useState<ExerciseFiltersObject>(filters);

  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const toggleZone = (zone: Assort) => {
    setDraft((current) => ({
      ...current,
      zones: getId(current.zones) === getId(zone) ? emptyFilters.zones : zone,
    }));
  };

  const handleApply = () => {
    setFilters(draft);
    setOpen(false);
  };

  const groupOptions = [
    { value: ALL_OPTION, label: t("wszystkie_zajecia", "Wszystkie zajęcia") },
    ...exerciseGroups.map((group) => ({
      value: getId(group),
      label: group.name,
    })),
  ];

  const instructorOptions = [
    { value: ALL_OPTION, label: t("wszyscy_instruktorzy", "Wszyscy instruktorzy") },
    ...humans.map((instructor) => ({
      value: getId(instructor),
      label: `${instructor.firstName ?? ""} ${instructor.lastName ?? ""}`.trim(),
    })),
  ];

  return (
    <EAModal
      isOpen={open}
      onClose={() => setOpen(false)}
      title={t("filtry")}
      acceptButtonLabel={t("zapisz", "Zapisz")}
      cancelButtonLabel={t("zamknij", "Zamknij")}
      onAccept={handleApply}
      desktopWidth="480px"
      stickyFooter
    >
      <div className="schedule-filters-modal">
        <div className="schedule-filters-modal__section">
          <EACheckbox
            name="schedule-only-free-spots"
            checked={Boolean(draft.onlyWithFreeSpots)}
            onChange={(checked) => setDraft({ ...draft, onlyWithFreeSpots: checked })}
            label={t("schedule_only_free_spots")}
          />
        </div>

        {exerciseGroups.length > 0 && (
          <div className="schedule-filters-modal__section">
            <EASelect
              label={t("wybierz_zajecia")}
              name="schedule-group"
              value={getId(draft.exerciseGroups) || ALL_OPTION}
              onChange={(event) => {
                const selected = exerciseGroups.find((group) => getId(group) === event.target.value);
                setDraft({ ...draft, exerciseGroups: selected ?? emptyFilters.exerciseGroups });
              }}
              options={groupOptions}
              fullWidth
            />
          </div>
        )}

        <div className="schedule-filters-modal__section">
          <EASelect
            label={t("wybierz_instruktora")}
            name="schedule-instructor"
            value={getId(draft.humans) || ALL_OPTION}
            onChange={(event) => {
              const selected = humans.find((instructor: Instructor) => getId(instructor) === event.target.value);
              setDraft({ ...draft, humans: selected ?? emptyFilters.humans });
            }}
            options={instructorOptions}
            fullWidth
          />
        </div>

        {zones.length > 0 && (
          <div className="schedule-filters-modal__section">
            <p className="schedule-filters-modal__label">{t("wybierz_strefy")}</p>
            <div className="schedule-filters-modal__options">
              {zones.map((zone) => (
                <EACheckbox
                  key={getId(zone)}
                  name={`schedule-zone-${getId(zone)}`}
                  checked={getId(draft.zones) === getId(zone)}
                  onChange={() => toggleZone(zone)}
                  label={zone.name}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </EAModal>
  );
};

export default ExerciseFltersModal;
